import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { AlertTriangle, Eye, EyeOff, Loader2, ArrowLeft, Sparkles } from 'lucide-react';
import VerifyCodePage from './VerifyCodePage';

export default function LoginPage({ onSwitch }) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pending2faEmail, setPending2faEmail] = useState(null);
  const [earlyBirdPending, setEarlyBirdPending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEarlyBirdPending(false);
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (!result.success) {
      if (result.error?.includes('Early Bird') || result.error?.includes('geprüft')) {
        setEarlyBirdPending(true);
      } else {
        setError(result.error);
      }
      return;
    }
    if (result.needs_2fa) {
      setPending2faEmail(result.email || email);
    }
  };

  if (pending2faEmail) {
    return (
      <VerifyCodePage
        email={pending2faEmail}
        purpose="login"
        onBack={() => setPending2faEmail(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 relative">
      <button onClick={() => onSwitch('landing')} data-testid="login-back-btn"
        className="absolute top-6 left-6 flex items-center gap-2 text-zinc-500 hover:text-zinc-200 text-sm font-mono transition-colors">
        <ArrowLeft className="w-4 h-4" /> Zurück zur Webseite
      </button>
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="LAIzyBlueprint" className="h-24 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-white font-mono tracking-wider">Anmelden</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          {earlyBirdPending && (
            <div data-testid="early-bird-pending" className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg text-center">
              <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-amber-300 font-mono text-sm font-medium mb-1">Zugang wird geprüft</p>
              <p className="text-zinc-400 font-mono text-xs leading-relaxed">Ihr Early Bird Zugang wird derzeit von unserem Team freigeschaltet. Bitte gedulden Sie sich.</p>
            </div>
          )}
          {error && (
            <div data-testid="login-error" className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}
          <div>
            <label className="block text-xs text-zinc-500 mb-1 font-mono">E-Mail</label>
            <input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email"
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm font-mono focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="relative">
            <label className="block text-xs text-zinc-500 mb-1 font-mono">Passwort</label>
            <input data-testid="login-password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password"
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm font-mono focus:border-blue-500 focus:outline-none pr-10" />
            <button type="button" onClick={() => setShowPw(p => !p)} className="absolute right-3 top-7 text-zinc-500 hover:text-zinc-300">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className="text-right">
            <button type="button" onClick={() => onSwitch('forgot-password')} data-testid="forgot-password-link"
              className="text-blue-400 hover:text-blue-300 text-xs font-mono transition-colors">
              Passwort vergessen?
            </button>
          </div>
          <button data-testid="login-submit" type="submit" disabled={loading}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white font-mono text-sm rounded-lg transition-colors flex items-center justify-center gap-2">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Anmelden...</> : 'Anmelden'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <button data-testid="switch-register" onClick={() => onSwitch('register')} className="text-blue-400 hover:text-blue-300 text-sm font-mono transition-colors">
            Noch kein Konto? Registrieren
          </button>
        </div>
      </div>
    </div>
  );
}
